import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  LogOut,
  Map,
  Users,
  Calendar,
  MessageSquare,
  Home,
  Settings,
  BarChart,
} from 'lucide-react';

const OperationsDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const dashboardCards = [
    {
      title: 'Flight Route Planner',
      description: 'Calculate distances and plan flight routes across India',
      icon: Map,
      path: '/operations/flight-planner',
      color: 'bg-blue-500',
    },
    {
      title: 'Event Management',
      description: 'Manage upcoming joy ride events and bookings',
      icon: Calendar,
      path: '/operations/events',
      color: 'bg-green-500',
    },
    {
      title: 'Customer Enquiries',
      description: 'View and respond to customer enquiries',
      icon: MessageSquare,
      path: '/operations/enquiries',
      color: 'bg-purple-500',
    },
    {
      title: 'Reports & Analytics',
      description: 'View business metrics and flight statistics',
      icon: BarChart,
      path: '/operations/reports',
      color: 'bg-orange-500',
    },
  ];

  const adminCards = [
    {
      title: 'User Management',
      description: 'Manage operations team members and access',
      icon: Users,
      path: '/operations/admin/users',
      color: 'bg-red-500',
    },
    {
      title: 'System Settings',
      description: 'Configure system settings and preferences',
      icon: Settings,
      path: '/operations/admin/settings',
      color: 'bg-gray-500',
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_dda025fc-cd40-4797-8c5d-d84f34dad8eb/artifacts/i3r7o5v7_HighKraft.jpeg" 
                alt="HighKraft Aviation Logo" 
                className="w-12 h-12 object-contain"
              />
              <div>
                <h1 className="text-xl font-bold text-aviation-blue">
                  Operations Dashboard
                </h1>
                <p className="text-sm text-aviation-blue/70">
                  Welcome, {user?.name}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={() => window.open('/', '_blank')}
                className="border-aviation-blue text-aviation-blue hover:bg-aviation-blue hover:text-white"
              >
                <Home className="w-4 h-4 mr-2" />
                Main Site
              </Button>
              <Button
                variant="outline"
                onClick={handleLogout}
                className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        {/* User Info Card */}
        <Card className="mb-8 border-l-4 border-aviation-gold">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-aviation-blue mb-2">
                  Welcome back, {user?.name}!
                </h2>
                <p className="text-aviation-blue/70">
                  Role: <span className="font-semibold capitalize">{user?.role}</span>
                </p>
                <p className="text-sm text-aviation-blue/60">
                  Email: {user?.email}
                </p>
              </div>
              <div className="hidden md:block">
                <div className="w-20 h-20 bg-aviation-blue/10 rounded-full flex items-center justify-center">
                  <span className="text-3xl font-bold text-aviation-blue">
                    {user?.name?.charAt(0).toUpperCase()}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Operations Tools */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-aviation-blue mb-4">
            Operations Tools
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {dashboardCards.map((card, index) => (
              <Card
                key={index}
                className="hover:shadow-xl transition-all cursor-pointer group"
                onClick={() => navigate(card.path)}
              >
                <CardContent className="pt-6">
                  <div
                    className={`w-12 h-12 ${card.color} rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}
                  >
                    <card.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-aviation-blue mb-2">
                    {card.title}
                  </h3>
                  <p className="text-sm text-aviation-blue/70">
                    {card.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Admin Tools (Only for Admin) */}
        {isAdmin && (
          <div>
            <h2 className="text-2xl font-bold text-aviation-blue mb-4">
              Admin Tools
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {adminCards.map((card, index) => (
                <Card
                  key={index}
                  className="hover:shadow-xl transition-all cursor-pointer group border-l-4 border-red-500"
                  onClick={() => navigate(card.path)}
                >
                  <CardContent className="pt-6">
                    <div
                      className={`w-12 h-12 ${card.color} rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}
                    >
                      <card.icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-aviation-blue mb-2">
                      {card.title}
                    </h3>
                    <p className="text-sm text-aviation-blue/70">
                      {card.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OperationsDashboard;
